import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { SpeechResultType, StatusType, TagType } from "App.types";
import io, { Socket } from "socket.io-client";
import Cookies from "js-cookie";
import { ACTIVE_LANG, API_URL, isNewIOS } from "App.constants";
import { flatten, last, uniq } from "lodash";
import { UserContext } from "App";
import { PlayerContext } from "PlayerContext";
import { notification } from "antd";
import wordsToNumbers from "words-to-numbers";

type Props = {
  tags?: Partial<TagType>[];
  setStatus: Dispatch<SetStateAction<StatusType>>;
  interim_results?: boolean;
  text: string | undefined;
  easy?: boolean;
};

const useSocketSpeech = ({
  setStatus,
  text = "",
  interim_results,
  easy,
}: Props): {
  start: () => any;
  stop: () => any;
  isSpeaking: boolean;
  results: SpeechResultType[];
  reset: () => any;
} => {
  const [results, setResults] = useState<SpeechResultType[]>([]);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [popupResults, setPopupResults] = useState(false);

  const userMedia = useRef<MediaStream>();
  const socket = useRef<Socket>();
  const microphone = useRef<MediaRecorder>();

  const user = useContext(UserContext);

  const token = user?.token || Cookies.get("token");

  const silentTimeoutId = useRef<any>(null);
  const finalTimeoutId = useRef<any>(null);

  const { onSpeechStart } = useContext(PlayerContext);

  useEffect(() => {
    if (user?.isAdmin && results.some((r) => r.transcript.includes("speech debug"))) {
      setPopupResults(true);
    }
  }, [results, user?.isAdmin]);

  useEffect(() => {
    if (popupResults && results.length > 0) {
      notification.destroy("speech");
      notification.info({ key: "speech", message: results.map((r) => r.transcript).join(", ") });
    }
  }, [popupResults, results]);

  const prevSpeechResultRef = useRef<string>("");

  console.log(results);

  // ios
  useEffect(() => {
    if (!isNewIOS()) return;

    const handleIosNativeSpeech = ({ detail: { transcript } }: CustomEvent<{ transcript: string }>) => {
      if (transcript === prevSpeechResultRef.current) return;

      clearTimeout(silentTimeoutId.current);
      silentTimeoutId.current = setTimeout(() => {
        setResults([]);
        setIsSpeaking(false);
        prevSpeechResultRef.current = "";
        setStatus((prevState) => (prevState !== StatusType.Completed ? StatusType.Empty : prevState));
        // @ts-ignore
        window?.webkit?.messageHandlers?.speech?.postMessage(JSON.stringify({ operation: "stop", hints: [], lang: "" }));
      }, 8000);

      clearTimeout(finalTimeoutId.current);
      finalTimeoutId.current = setTimeout(() => {
        setResults((prev) => prev.map((r) => ({ ...r, isFinal: true })));
      }, 1000);

      const lastNumericWord = last(transcript.split(" "))?.replace(/\D/g, "") ?? "";
      const lastPrevWord = wordsToNumbers(last(prevSpeechResultRef.current?.split(" ")) ?? "")?.toString() ?? "";
      const lastWordDifference = lastNumericWord.replace(lastPrevWord, "");
      const match = lastNumericWord && lastWordDifference.match(/\d/);

      const defaultResult = {
        transcript,
        confidence: 1,
        isFinal: false,
        words: transcript.split(" ").map((word) => ({ word, confidence: 1, start: 0, end: 0, punctuated_word: word })),
      };

      setResults((prev) =>
        match
          ? [
              {
                transcript: `${prev[0]?.transcript} ${lastWordDifference}`,
                confidence: 1,
                isFinal: false,
                words: `${prev[0]?.transcript} ${lastWordDifference}`
                  .split(" ")
                  .map((word) => ({ word, confidence: 1, start: 0, end: 0, punctuated_word: word })),
              },
              defaultResult,
            ]
          : [defaultResult],
      );
      prevSpeechResultRef.current = transcript;
    };
    const handleNativeError = (err: any) => {
      console.error("native-speech-stop", err);
      setStatus((prevState) => (prevState !== StatusType.Completed ? StatusType.Empty : prevState));
      setIsSpeaking(false);
      prevSpeechResultRef.current = "";
    };

    window.addEventListener("native-speech", handleIosNativeSpeech as EventListener);
    window.addEventListener("native-speech-stop", handleNativeError as EventListener);

    const closeNativeSpeech = () => {
      console.log("closeNativeSpeech");
      //@ts-ignore
      window?.webkit?.messageHandlers?.speech?.postMessage(JSON.stringify({ operation: "stop", hints: [], lang: "" }));
    };

    window.addEventListener("unload", closeNativeSpeech);

    return () => {
      closeNativeSpeech();
      clearTimeout(silentTimeoutId.current);
      clearTimeout(finalTimeoutId.current);
      window.removeEventListener("native-speech", handleIosNativeSpeech as EventListener);
      window.removeEventListener("native-speech-stop", handleNativeError as EventListener);
      window.removeEventListener("unload", closeNativeSpeech);
    };
  }, [setStatus]);

  const keywords = useMemo(
    () =>
      uniq(
        text?.split(" ").reduce(
          (acc, cur, idx, arr) => {
            return flatten([...acc, Array.from(Array(arr.length).keys()).map((n) => arr.slice(idx, n + 1).join(" "))]);
          },
          [text],
        ),
      ).filter((s) => s),
    [text],
  );

  const onDataAvailable = useCallback(async (event: any) => {
    if (event.data.size > 0 && socket.current?.connected) {
      console.log("audio sent");
      socket.current?.emit("speech", event.data);
    }
  }, []);

  const stop = useCallback(() => {
    console.log("STOP");
    setPopupResults(false);
    prevSpeechResultRef.current = "";
    setResults([]);
    setIsSpeaking(false);
    clearTimeout(silentTimeoutId.current);

    if (isNewIOS()) {
      clearTimeout(finalTimeoutId.current);
      //@ts-ignore
      window?.webkit?.messageHandlers?.speech?.postMessage(JSON.stringify({ operation: "stop", hints: [""], lang: "" }));
    } else {
      // recordRtc
      // microphone.current?.stopRecording();
      microphone.current?.removeEventListener("dataavailable", onDataAvailable);
      socket.current?.disconnect();

      microphone.current?.stop();
      if (userMedia.current) userMedia?.current.getTracks().forEach((track) => track.stop());
    }

    setStatus((prevState) => (prevState !== StatusType.Completed ? StatusType.Empty : prevState));
  }, [setStatus, onDataAvailable]);

  const start = useCallback(async () => {
    clearTimeout(silentTimeoutId.current);
    silentTimeoutId.current = setTimeout(stop, 8000);

    onSpeechStart?.();
    setStatus(StatusType.Loading);
    setResults([]);

    if (isNewIOS()) {
      //@ts-ignore
      window?.webkit?.messageHandlers?.speech?.postMessage(
        JSON.stringify({ lang: ACTIVE_LANG.value, hints: keywords, operation: "start" }),
      );
      console.log("ios-speech-start");
      setStatus(StatusType.IsRecording);
    } else {
      try {
        if ("audioSession" in navigator) {
          // @ts-ignore
          navigator.audioSession.type = "play-and-record";
        }
      } catch (e) {
        console.error(e);
      }
      if (!socket.current || !socket.current?.connected) {
        socket.current = io(`${new URL(API_URL).protocol}//${new URL(API_URL).hostname}/ggl`, {
          query: {
            keywords: `${keywords.join(";")}`,
            // keywords: `${text
            //   ?.replace(/[,.!?:-]/g, "")
            //   .split(" ")
            //   .join(";")}`,
            token,
            interim_results,
            easy,
          },
        });

        socket.current.on("connect", async () => {
          console.log("client: connected to websocket");

          userMedia.current = await navigator.mediaDevices.getUserMedia({
            audio: { channelCount: 1, sampleRate: 16000 },
          });
          if (!userMedia) return console.error("no UserMedia available");

          setStatus(StatusType.IsRecording);

          microphone.current = new MediaRecorder(userMedia.current);

          // for openai stream transcriber
          // microphone.current = new RecordRTC(userMedia.current, {
          //   type: "audio",
          //   mimeType: "audio/webm;codecs=pcm",
          //   recorderType: RecordRTC.StereoAudioRecorder,
          //   timeSlice: 250,
          //   desiredSampRate: 24000,
          //   numberOfAudioChannels: 1,
          //   bufferSize: 4096,
          //   audioBitsPerSecond: 128000,
          //   ondataavailable: (blob) => {
          //     if (!socket.current?.connected) return;
          //     socket.current?.emit("speech", blob);
          //   },
          // });
          //microphone.current.startRecording();

          microphone.current?.addEventListener("dataavailable", onDataAvailable);
          microphone.current?.start(100);
        });

        socket.current.on("dg", (message: SpeechResultType[]) => {
          clearTimeout(silentTimeoutId.current);
          silentTimeoutId.current = setTimeout(stop, 8000);

          console.log(
            "dg:",
            message.map((m) => m.transcript),
          );

          setResults(message);
        });

        socket.current.on("disconnect", () => {
          console.log("client: disconnected");
          setIsSpeaking(false);
          microphone.current?.removeEventListener("dataavailable", onDataAvailable);

          setStatus((prevState) => (prevState !== StatusType.Completed ? StatusType.Empty : prevState));
          microphone.current?.stop();
          //microphone.current.stopRecording();
          if (userMedia.current) userMedia?.current.getTracks().forEach((track) => track.stop());
        });

        socket.current.on("SpeechStarted", (message) => {
          setIsSpeaking(message);
        });

        socket.current.on("g", (messages) => {
          clearTimeout(silentTimeoutId.current);
          silentTimeoutId.current = setTimeout(stop, 8000);

          console.log(
            "g: ",
            messages.map((m: SpeechResultType) => m.transcript),
          );

          setResults(messages.map((m: SpeechResultType) => ({ ...m, words: m.transcript.split(" ").map((word) => ({ word })) })));
        });
      }
    }
  }, [onSpeechStart, setStatus, stop, keywords, token, interim_results, easy, onDataAvailable]);

  useEffect(() => {
    setStatus((prev) => {
      if (prev === StatusType.IsRecording && keywords.length) {
        console.log("RECONNECT");
        setResults([]);

        if (isNewIOS()) {
          //@ts-ignore
          window?.webkit?.messageHandlers?.speech?.postMessage(JSON.stringify({ operation: "reset", hints: keywords, lang: "" }));
        } else {
          stop();
          start();
        }
      }

      return prev;
    });
  }, [keywords, stop, start, setStatus]);

  const reset = useCallback(() => {
    setResults([]);
  }, []);

  return { start, stop, isSpeaking, results, reset };
};

export default useSocketSpeech;
